<template>
  <div class="app">
    <div class="modern-card">
      <!-- Header Section -->
      <div 
        v-if="!showHomePage" 
        class="modern-header">
        <div class="header-content">
          <h3 class="outlet-name">{{ outlet.name }}</h3>
          <p 
            v-if="contact.contact_number" 
            class="contact-info">
            <svg 
              width="16" 
              height="16" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            Contact No. {{ contact.contact_number }}
          </p>
          <p class="portal-title">E-Invoice Request Portal</p>
        </div>
      </div>
  
      <!-- Home Page -->
      <div 
        v-if="showHomePage" 
        class="home-section">
        <div class="home-content">
          <div class="home-icon">
            <svg 
              width="48" 
              height="48" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line 
                x1="16" 
                y1="13" 
                x2="8" 
                y2="13"/>
              <line 
                x1="16" 
                y1="17" 
                x2="8" 
                y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
          </div>
          <h3 class="home-title">{{ outlet.name }}</h3>
          <p class="home-subtitle">E-Invoice Request Portal</p>
          <p class="home-description">Generate and manage your electronic invoices with ease</p>
        </div>
      </div>
  
      <!-- Step Content -->
      <div 
        v-else 
        class="step-content">
        <component
          :is="currentStepComponent"
          :form-data="currentFormData"
          :settled-date="receiptDateFormat(receipt.settled_date)"
          :invoice-no="receiptNumberFormat(receipt)"
          :grand-total="receipt.total_amount"
          :already-submitted="alreadySubmitted"
          :is-expired="isExpired"
          :validation-date="receiptDateFormat(validationDate)"
          :unique-identifier="uniqueIdentifier"
          :status="status"
          :validation-link="validationLink"
          :franchise-id="franchiseId"
          :transaction-id="transaction.id"
          :einvoice-customer="einvoiceCustomer"
          :customer-info="formData.contact"
          :consolidated-id="consolidatedId"
          @next="nextStep"
          @previous="previousStep"
          @cancel="cancel"
          @submit-data="collectData"
          @validate-data="validateData"
          @submit-einvoice-email="submitInvoiceEmail"
        />
      </div>
  
      <!-- Home Footer -->
      <div 
        v-if="showHomePage" 
        class="home-footer">
        <button 
          class="modern-primary-btn" 
          @click="showVerificationSteps">
          <svg 
            width="20" 
            height="20" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
          </svg>
          View e-Invoice
        </button>
      </div>
    </div>
  </div>
</template>
  
<script>
import VerifyInformation from './verify-info.vue';
import BuyerInformation from './buyer-info.vue';
import FinalizeRequest from './finalize-request.vue';
import receiptMixin from '../../../mixins/receipt';

export default {
    components: {
        VerifyInformation,
        BuyerInformation,
        FinalizeRequest,
    },
    mixins: [receiptMixin],
    props: {
        outlet: {
            type: Object,
            required: true,
        },
        contact: {
            type: Object,
            required: true,
        },
        transaction: {
            type: Object,
            required: true,
        },
        receipt: {
            type: Object,
            required: true,
        },
        franchiseId: {
            type: String,
            required: true,
        },
        encFranchiseId: {
            type: String,
            required: true,
        },
        encTransactionId: {
            type: String,
            required: true,
        },
        alreadySubmitted: {
            type: Boolean,
            required: true,
        },
        isExpired: {
            type: Boolean,
            required: true,
        },
        validationDate: {
            type: String,
            default: null,
        },
        uniqueIdentifier: {
            type: String,
            default: null,
        },
        status: {
            type: String,
            default: null,
        },
        validationLink: {
            type: String,
            default: null,
        },
        idType: {
            type: String,
            default: '',
        },
        einvoiceCustomer: {
            type: Object,
            default: null,
        },
        consolidatedId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            showHomePage: true,
            currentStep: 0,
            stepTitles: ['', 'Buyer Information', ''],
            formData: {
                transaction_id: this.transaction.id,
                contact: {
                    idType: 'NRIC',
                },
                registration: {},
                softcopy_email: {},
            },
        };
    },
    computed: {
        currentStepComponent() {
            const steps = [
                'VerifyInformation',
                'BuyerInformation',
                'FinalizeRequest',
            ];
            return steps[this.currentStep];
        },
        currentStepTitle() {
            return this.stepTitles[this.currentStep] || '';
        },
        currentFormData() {
            switch (this.currentStep) {
                case 1:
                    return this.formData.contact;
                case 2:
                    return this.formData.softcopy_email;
                default:
                    return {};
            }
        },
    },
    mounted() {
        this.$emit('update-step-title', this.currentStepTitle);
    },
    methods: {
        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });

            // Alternative: scroll to top of the card container
            const cardElement = this.$el.querySelector('.modern-card');
            if (cardElement) {
                cardElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        },
        showVerificationSteps() {
            this.showHomePage = false;
            this.$nextTick(() => {
                this.scrollToTop();
            });
        },
        nextStep() {
            if (this.currentStep < this.stepTitles.length - 1) {
                this.currentStep++;
                this.$nextTick(() => {
                    this.scrollToTop();
                });
            }
        },
        previousStep() {
            if (this.currentStep > 0) {
                this.currentStep--;
                this.$nextTick(() => {
                    this.scrollToTop();
                });
            }
        },
        cancel() {
            window.location.href = `/e-inv/${this.encFranchiseId}/${
                this.encTransactionId
            }`;
        },
        collectData(data) {
            if (this.currentStep === 1) {
                this.formData.contact = data;
            } else if (this.currentStep === 2) {
                this.formData.softcopyEmail = data;
            }
            this.nextStep();
        },
        submitInvoiceEmail() {
            const emailData = {
                franchise_id: this.franchiseId,
                outlet_name: this.outlet.name,
                transaction_id: this.receipt.transaction_id,
                receipt_no: this.receiptNumberFormat(this.receipt),
                receipt_date: this.receiptDateFormat(this.receipt.settled_date),
                grand_total: this.receipt.total_amount,
                softcopy_email: this.formData.softcopyEmail.softcopy_email,
            };

            axios
                .post(
                    Laroute.route('sendEmailSubmittedEInvoice', {
                        id: this.franchiseId,
                    }),
                    emailData,
                )
                .then(response => {
                    // Success
                })
                .catch(error => {
                    // Error
                });
        },
        validateData() {
            const combinedData = {
                franchise_id: this.franchiseId,
                transaction_id: this.receipt.transaction_id,
                receipt_date: this.receiptDateFormat(this.receipt.settled_date),
                name: this.formData.contact.name,
                email: this.formData.contact.email,
                contact_no: this.formData.contact.contact_no,
                sst_reg_no: this.formData.contact.sst_reg_no,
                enter_id: this.formData.contact.enter_id,
                address: this.formData.contact.address,
                address_2: this.formData.contact.address_2,
                address_3: this.formData.contact.address_3,
                postal_zone: this.formData.contact.postal_zone,
                city: this.formData.contact.city,
                state: this.formData.contact.stateCode,
                country: this.formData.contact.countryCode,
                id_no: this.formData.contact.id_no,
                invoice_no: this.receiptNumberFormat(this.receipt),
                id_type: this.formData.contact.idType,
                softcopy_email: this.formData.softcopyEmail.softcopy_email,
            };

            // Note: PDF email will now be sent from backend after successful validation
            this.swalAjaxConfirmation({
                title: 'Validate Request',
                text: 'Are you sure to submit this request?',
                method: 'post',
                type: 'warning',
                params: combinedData,
                url: '/e-invoice-request/makeValidate',
                onSuccess: res => {
                    if (res.value.data.code === 400) {
                        const responseError = res.value.data.response[0].error;
                        const detailMessage =
                            responseError.details &&
                            responseError.details.length > 0
                                ? responseError.details[0].message
                                : 'Something went wrong. Please try again.';

                        swal({
                            title: 'Warning',
                            text: detailMessage,
                            type: 'info',
                            confirmButtonColor: '#ef5350',
                            confirmButtonText: 'Close',
                        });
                    } else {
                        // PDF and validation emails will be sent from backend
                        swal({
                            title: 'Success',
                            text: 'Your data has been submitted successfully!',
                            type: 'success',
                            confirmButtonColor: '#ef5350',
                            confirmButtonText: 'Close',
                        }).then(() => {
                            this.cancel();
                        });
                    }
                },
                onError: err => {
                    if (err.response && err.response.code === 400) {
                        const detailMessage =
                            err.response.data.details &&
                            err.response.data.details.length > 0
                                ? err.response.data.details[0].message
                                : 'Something went wrong. Please try again.';

                        swal({
                            title: 'Warning',
                            text: detailMessage,
                            type: 'info',
                            confirmButtonColor: '#ef5350',
                            confirmButtonText: 'Close',
                        });
                    } else {
                        this.swalError({
                            title: 'Validation Error',
                            text:
                                'An error occurred during validation. Please try again.',
                        });
                    }
                },
            });
        },
    },
};
</script>
  
  <style scoped>
.app {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
        'Helvetica Neue', Arial, sans-serif;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    align-items: center;
    min-height: 100vh;
    background: linear-gradient(135deg, #e5e8f5 0%, #d87c7c 100%);
    padding: 20px;
    box-sizing: border-box;
}

.modern-card {
    width: 100%;
    max-width: 600px;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    background: #fff;
    overflow: hidden;
    position: relative;
}

.modern-header {
    background: linear-gradient(135deg, #ef5350 0%, #e53935 100%);
    color: #fff;
    position: relative;
    overflow: hidden;
}

.modern-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
        repeat;
}

.header-content {
    position: relative;
    z-index: 1;
    padding: 24px;
    text-align: center;
}

.outlet-name {
    margin: 0 0 8px 0;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.025em;
}

.contact-info {
    margin: 0 0 8px 0;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    opacity: 0.95;
}

.portal-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 500;
    opacity: 0.9;
}

.home-section {
    padding: 48px 32px;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.home-content {
    text-align: center;
    max-width: 400px;
}

.home-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #ef5350 0%, #e53935 100%);
    border-radius: 20px;
    color: white;
    margin-bottom: 24px;
    box-shadow: 0 8px 32px rgba(239, 83, 80, 0.3);
}

.home-title {
    margin: 0 0 8px 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.025em;
}

.home-subtitle {
    margin: 0 0 12px 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #ef5350;
}

.home-description {
    margin: 0;
    font-size: 1rem;
    color: #64748b;
    line-height: 1.6;
}

.step-content {
    flex: 1;
    background: #fff;
}

.home-footer {
    padding: 32px;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    border-top: 1px solid #e2e8f0;
}

.modern-primary-btn {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, #ef5350 0%, #e53935 100%);
    color: #fff;
    border: none;
    padding: 16px 32px;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 16px rgba(239, 83, 80, 0.3);
    position: relative;
    overflow: hidden;
}

.modern-primary-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
    );
    transition: left 0.5s;
}

.modern-primary-btn:hover::before {
    left: 100%;
}

.modern-primary-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(239, 83, 80, 0.4);
}

.modern-primary-btn:active {
    transform: translateY(0);
}

@media (max-width: 768px) {
    .app {
        padding: 12px;
    }

    .modern-card {
        max-width: 100%;
        border-radius: 12px;
    }

    .header-content {
        padding: 20px 16px;
    }

    .outlet-name {
        font-size: 1.25rem;
    }

    .home-section {
        padding: 32px 20px;
        min-height: 300px;
    }

    .home-icon {
        width: 64px;
        height: 64px;
        border-radius: 16px;
    }

    .home-title {
        font-size: 1.5rem;
    }

    .home-subtitle {
        font-size: 1.1rem;
    }

    .home-footer {
        padding: 24px 20px;
    }

    .modern-primary-btn {
        padding: 14px 28px;
        font-size: 1rem;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/e-invoice/portal/e-invoice-request.vue